const PROFESSIONALISM_SCALE = [
  {
    key: 'c',
    category: 'C',
    label: 'Pas professionnelle',
    level: 'Tres faible',
    min: 0,
    max: 20,
    weight: 20,
    bgClass: 'bg-red-600',
    textClass: 'text-[#B3372A]',
    badgeClass: 'bg-[#FCEDEB] text-[#B3372A]',
    hex: '#DC2626',
    // La couleur vive est faite pour les aplats : en texte sur blanc elle
    // tombe sous le seuil (le jaune a 1,53). Celle-ci passe.
    hexTexte: '#B3372A',
    glow: 'rgba(220, 38, 38, 0.24)',
  },
  {
    key: 'b',
    category: 'B',
    label: 'Un peu professionnelle',
    level: 'Faible',
    min: 21,
    max: 49,
    weight: 29,
    bgClass: 'bg-orange-500',
    textClass: 'text-[#9A5300]',
    badgeClass: 'bg-[#FDF3E3] text-[#9A5300]',
    hex: '#F97316',
    // La couleur vive est faite pour les aplats : en texte sur blanc elle
    // tombe sous le seuil (le jaune a 1,53). Celle-ci passe.
    hexTexte: '#9A5300',
    glow: 'rgba(249, 115, 22, 0.24)',
  },
  {
    key: 'b_plus',
    category: 'B+',
    label: 'Moyennement professionnelle',
    level: 'Moyen',
    min: 50,
    max: 70,
    weight: 21,
    bgClass: 'bg-yellow-400',
    textClass: 'text-[#7A5C00]',
    badgeClass: 'bg-[#FBF6DF] text-[#7A5C00]',
    hex: '#FACC15',
    // La couleur vive est faite pour les aplats : en texte sur blanc elle
    // tombe sous le seuil (le jaune a 1,53). Celle-ci passe.
    hexTexte: '#7A5C00',
    glow: 'rgba(250, 204, 21, 0.24)',
  },
  {
    key: 'a',
    category: 'A',
    label: 'Professionnelle',
    level: 'Bon',
    min: 71,
    max: 90,
    weight: 20,
    bgClass: 'bg-[#5EBD4F]',
    textClass: 'text-[#2F7A2A]',
    badgeClass: 'bg-[#EAF6E7] text-[#2F7A2A]',
    hex: '#5EBD4F',
    // La couleur vive est faite pour les aplats : en texte sur blanc elle
    // tombe sous le seuil (le jaune a 1,53). Celle-ci passe.
    hexTexte: '#2F7A2A',
    glow: 'rgba(94, 189, 79, 0.24)',
  },
  {
    key: 'a_plus',
    category: 'A+',
    label: 'Tres professionnelle',
    level: 'Acquis',
    min: 91,
    max: 100,
    weight: 10,
    bgClass: 'bg-emerald-600',
    textClass: 'text-[#2F7A2A]',
    badgeClass: 'bg-[#E3F4EC] text-[#046B4D]',
    hex: '#059669',
    // La couleur vive est faite pour les aplats : en texte sur blanc elle
    // tombe sous le seuil (le jaune a 1,53). Celle-ci passe.
    hexTexte: '#046B4D',
    glow: 'rgba(5, 150, 105, 0.24)',
  },
];

const CATEGORY_ALIASES = {
  c: 'C',
  b: 'B',
  bplus: 'B+',
  a: 'A',
  aplus: 'A+',
};

const SYNONYM_TO_CATEGORY = [
  { test: /pas professionnelle|initial|novice|debut/i, category: 'C' },
  { test: /un peu professionnelle|faible/i, category: 'B' },
  { test: /moyennement professionnelle|intermediaire|moyen/i, category: 'B+' },
  { test: /professionnelle|confirme|bon/i, category: 'A' },
  { test: /tres professionnelle|expert|acquis/i, category: 'A+' },
];

const SCALE_BY_CATEGORY = Object.freeze(
  PROFESSIONALISM_SCALE.reduce((acc, item) => ({ ...acc, [item.category]: item }), {})
);

export const PROFESSIONALISM_LEVELS = Object.freeze(
  PROFESSIONALISM_SCALE.map((item) => Object.freeze({ ...item }))
);

export const clampProfessionalismScore = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

export const getProfessionalismInfoByCategory = (category, fallbackScore = 0) => {
  const fallback = getProfessionalismInfo(fallbackScore);
  if (!category) return fallback;

  const normalized = String(category).toLowerCase().replace(/[^a-z+]/g, '');
  const alias = CATEGORY_ALIASES[normalized];
  if (alias && SCALE_BY_CATEGORY[alias]) return SCALE_BY_CATEGORY[alias];

  const direct = SCALE_BY_CATEGORY[String(category).toUpperCase()];
  if (direct) return direct;
  return fallback;
};

export const getProfessionalismInfo = (score) => {
  const safeScore = clampProfessionalismScore(score);
  return (
    PROFESSIONALISM_LEVELS.find((item) => safeScore >= item.min && safeScore <= item.max)
    || PROFESSIONALISM_LEVELS[0]
  );
};

export const resolveProfessionalismInfo = ({ score, rawLevel, index } = {}) => {
  const safeIndex = Number.isFinite(Number(index)) ? Number(index) : -1;
  const text = String(rawLevel || '').trim();

  if (text) {
    const normalizedText = text.toLowerCase();

    if (/a\+|a plus/.test(normalizedText)) return getProfessionalismInfoByCategory('A+');
    if (/\bb\+\b|b plus/.test(normalizedText)) return getProfessionalismInfoByCategory('B+');
    if (/\bc\b/.test(normalizedText)) return getProfessionalismInfoByCategory('C');
    if (/\bb\b/.test(normalizedText)) return getProfessionalismInfoByCategory('B');
    if (/\ba\b/.test(normalizedText)) return getProfessionalismInfoByCategory('A');

    const numericMatch = normalizedText.match(/(\d+)/);
    if (numericMatch) {
      const numericRank = Number(numericMatch[1]);
      if (numericRank >= 1 && numericRank <= PROFESSIONALISM_LEVELS.length) {
        return PROFESSIONALISM_LEVELS[numericRank - 1];
      }
    }

    const synonymMatch = SYNONYM_TO_CATEGORY.find((item) => item.test.test(normalizedText));
    if (synonymMatch) return getProfessionalismInfoByCategory(synonymMatch.category);
  }

  if (Number.isFinite(Number(score))) return getProfessionalismInfo(score);
  if (safeIndex >= 0 && safeIndex < PROFESSIONALISM_LEVELS.length) return PROFESSIONALISM_LEVELS[safeIndex];
  return PROFESSIONALISM_LEVELS[0];
};

export const mapLevelNameToCategory = (rawLevel, score, index) => (
  resolveProfessionalismInfo({ rawLevel, score, index }).category
);

